// 3章 土工事 — やさしい解説
// 原文は src/spec_index.json（PDFから自動生成）。このファイルは手書きで管理する。
// SVGは currentColor を使い、ライト/ダーク両テーマに追従させる。

import type { Commentary } from "../types";

const SVG_NEGIRI = `
<svg viewBox="0 0 660 260" role="img" aria-label="根切りの断面。周辺状況や土質、地下水に適した工法とし、法面または山留めを設ける。根切り底は地盤をかく乱しないよう平たんに掘り、状態、土質、深さを確認して監督職員の検査を受ける">
  <g fill="currentColor" opacity="0.25">
    <path d="M40 80 H240 L320 200 H620 V240 H40 Z"/>
  </g>
  <g stroke="currentColor" stroke-width="2.5">
    <path d="M40 80 H240 L320 200 H620" fill="none"/>
  </g>
  <g stroke="currentColor" stroke-width="1.5" stroke-dasharray="6 4" opacity="0.8">
    <line x1="320" y1="200" x2="620" y2="200"/>
  </g>
  <g fill="currentColor" font-size="12.5">
    <text x="60" y="60" opacity="0.9">地盤面（GL）</text>
    <text x="215" y="130" font-weight="bold">法面（のりめん）</text>
    <text x="215" y="150" opacity="0.85">または山留めで崩壊防止</text>
    <text x="400" y="228" font-weight="bold">根切り底：かく乱しない・平たんに</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.85">
    <text x="400" y="100">・埋設管（水道・ガス・ケーブル）は</text>
    <text x="412" y="118">事前調査→監督職員に報告</text>
    <text x="400" y="144">・根切り底の状態・土質・深さ</text>
    <text x="412" y="162">→ 監督職員の検査</text>
  </g>
  <g fill="currentColor" font-size="11.5" opacity="0.75">
    <text x="40" y="256">かく乱してしまったら承諾を受け、自然地盤と同等以上の強度に処置。凍結にも注意（3.2.1）</text>
  </g>
</svg>`;

const SVG_KAMABA_DRAINAGE = `
<svg viewBox="0 0 660 260" role="img" aria-label="3.2.2 釜場排水。根切り底に排水溝で勾配をつけ、最も低い一角に設けた小さな集水桝の釜場へ雨水や湧き水を集め、水中ポンプと排水ホースで場外の沈砂槽へ排水する最も簡便な排水工法">
  <g fill="currentColor" opacity="0.18">
    <path d="M40 80 H620 V225 H40 Z"/>
  </g>
  <g stroke="currentColor" stroke-width="2.5" fill="none">
    <line x1="40" y1="80" x2="620" y2="80"/>
    <path d="M80 190 L500 215"/>
    <rect x="500" y="175" width="75" height="55"/>
    <path d="M535 195 C535 120 600 150 610 90"/>
  </g>
  <g fill="currentColor">
    <polygon points="475,205 455,196 456,216"/>
  </g>
  <g fill="currentColor" font-size="13">
    <text x="80" y="55" font-weight="bold">排水溝の勾配で水を集める</text>
    <text x="470" y="250">釜場（集水桝）</text>
    <text x="520" y="150">水中ポンプ</text>
    <text x="550" y="105">排水ホース</text>
  </g>
</svg>`;

const SVG_UMEMODOSHI = `
<svg viewBox="0 0 660 250" role="img" aria-label="埋戻しと盛土。300ミリ程度ごとに締め固め、土質に応じて余盛りを行う。材料はA種の山砂の類からD種の再生コンクリート砂まで種別がある">
  <g fill="currentColor" opacity="0.2"><rect x="60" y="180" width="320" height="40"/></g>
  <g fill="currentColor" opacity="0.3"><rect x="60" y="140" width="320" height="40"/></g>
  <g fill="currentColor" opacity="0.4"><rect x="60" y="100" width="320" height="40"/></g>
  <g fill="currentColor" opacity="0.5"><path d="M60 100 Q220 70 380 100 Z"/></g>
  <g stroke="currentColor" stroke-width="1.5">
    <line x1="400" y1="100" x2="400" y2="140"/>
    <line x1="392" y1="100" x2="408" y2="100"/>
    <line x1="392" y1="140" x2="408" y2="140"/>
  </g>
  <g fill="currentColor" font-size="12.5">
    <text x="420" y="124" font-weight="bold">1層 300mm程度ごとに締固め</text>
    <text x="420" y="150" opacity="0.85">（一気に埋めて上だけ固めても</text>
    <text x="430" y="168" opacity="0.85">　中はゆるゆる→後で沈下する）</text>
    <text x="90" y="66" font-weight="bold">余盛り（土質に応じて沈下分を見込む）</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8">
    <text x="60" y="242">材料の種別：A種=山砂の類（水締め可）／B種=根切り土の良質土／C種=他現場の良質土／D種=再生コンクリート砂</text>
  </g>
</svg>`;

const SVG_YAMADOME = `
<svg viewBox="0 0 660 280" role="img" aria-label="山留めの構成。左右の山留め壁が土圧を受け、壁に沿って腹起しを流し、腹起し同士を切りばりで突っ張って支える。設置期間中は点検と計測を行う">
  <g stroke="currentColor" stroke-width="8" stroke-linecap="round">
    <line x1="120" y1="50" x2="120" y2="240"/>
    <line x1="540" y1="50" x2="540" y2="240"/>
  </g>
  <g fill="currentColor" opacity="0.7">
    <rect x="128" y="90" width="16" height="120"/>
    <rect x="516" y="90" width="16" height="120"/>
  </g>
  <g stroke="currentColor" stroke-width="6" stroke-linecap="round">
    <line x1="144" y1="120" x2="516" y2="120"/>
    <line x1="144" y1="190" x2="516" y2="190"/>
  </g>
  <g stroke="currentColor" stroke-width="1.6" opacity="0.7">
    <line x1="60" y1="100" x2="104" y2="100"/><line x1="60" y1="160" x2="104" y2="160"/><line x1="60" y1="220" x2="104" y2="220"/>
    <line x1="600" y1="100" x2="556" y2="100"/><line x1="600" y1="160" x2="556" y2="160"/><line x1="600" y1="220" x2="556" y2="220"/>
  </g>
  <g fill="currentColor">
    <polygon points="104,100 92,94 92,106"/><polygon points="104,160 92,154 92,166"/><polygon points="104,220 92,214 92,226"/>
    <polygon points="556,100 568,94 568,106"/><polygon points="556,160 568,154 568,166"/><polygon points="556,220 568,214 568,226"/>
  </g>
  <g fill="currentColor" font-size="12.5" font-weight="bold" text-anchor="middle">
    <text x="330" y="110">切りばり（突っ張り）</text>
    <text x="330" y="180">切りばり</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.85">
    <text x="60" y="40">山留め壁（土圧を受け止める）</text>
    <text x="150" y="86">腹起し（壁の力を切りばりへ伝える横架材）</text>
    <text x="40" y="266">土圧 →（山留め壁）→ 腹起し → 切りばり の順に力が流れる。設置期間中は点検・計測を続ける（3.3.2）</text>
  </g>
</svg>`;

const ch03: Record<string, Commentary> = {
  "3.1.1": {
    plainSummary:
      "この章は根切り・排水・埋戻し・盛土・地均しの土工事と、山留め壁・切りばり・腹起しを用いる山留め工事のルール。1章とセットで適用する。",
    why: "土工事は「掘る（根切り）→守る（山留め・排水）→戻す（埋戻し・盛土）」の3ステップです。地面の下の工事はやり直しがきかず、事故も重大になりやすいため、検査と管理のポイントを押さえましょう。",
  },

  "3.1.2": {
    plainSummary:
      "ゴールは2つ。①根切りは所定の形状・寸法で、根切り底は上部構造物に有害な影響を与えない平たんな状態 ②埋戻し・盛土は所定材料で締め固められていること。",
  },

  "3.1.3": {
    plainSummary:
      "異常沈下・法面の滑動などの災害防止措置を講ずる。土砂運搬のこぼれ・飛散、泥土の流出を防ぎ、必要に応じて清掃・水洗い。掘削機械の騒音・振動にも配慮する。",
    points: [
      "場外へのタイヤ泥の持ち出しはクレームの定番。洗い場と清掃当番を決める",
      "隣接地の井戸・擁壁・ブロック塀は着工前に現況写真を撮っておく",
    ],
  },

  "3.2.1": {
    plainSummary:
      "根切りは土質・地下水に適した工法とし、法面または山留めを設ける。埋設管は事前調査して報告、掘り当てたら応急処置して協議。根切り底は地盤をかく乱せず平たんに掘り、状態・土質・深さの検査を受ける。",
    why: "根切り底は建物の全荷重を受ける面です。重機で掘りすぎてかき乱すと、その部分だけ支持力が落ちて不同沈下の原因になります。「最後は手掘り・床付けは丁寧に」が現場の鉄則。支持地盤が設計と違ったら即協議です。",
    points: [
      "床付け面は重機の爪跡を残さない（最後はバケットの平刃や人力で）",
      "埋設管調査（試掘・台帳照合）は掘削前に。ガス管損傷は重大事故",
      "根切り底の検査（土質・深さ）を受けてから次工程へ",
    ],
    glossary: [
      {
        term: "根切り（ねぎり）",
        meaning: "基礎や地下をつくるために地盤を掘削すること。",
      },
      {
        term: "床付け（とこづけ）",
        meaning: "根切り底を所定の深さで平たんに仕上げること。",
      },
      {
        term: "法面（のりめん）",
        meaning: "掘削でつくる斜面。崩れない勾配にするか山留めで守る。",
      },
    ],
    diagrams: [
      {
        svg: SVG_NEGIRI,
        caption: "根切りの基本（3.2.1）",
      },
    ],
  },

  "3.2.2": {
    plainSummary:
      "雨水・湧き水・たまり水は排水溝・集水桝を設けてポンプで排水する。予想外の出水は直ちに協議。場外へ放流する場合は沈砂槽等を設け、法令に基づき適切に放流する。",
    why: "水を溜めたまま作業すると、根切り底が緩み、法面が崩れます。また泥水をそのまま側溝に流すと水質汚濁で行政指導の対象になります。「現場の水はきれいにしてから帰す」が原則です。",
    points: [
      "放流先（下水道・側溝・河川）ごとに必要な手続きを確認",
      "台風前はポンプ・ホースの点検と予備の確保",
    ],
    glossary: [
      {
        term: "釜場（かまば）",
        meaning:
          "根切り底の一角に掘る水たまりのくぼみ。水を集めてポンプで排水する。釜場排水は最も簡便な排水工法。",
      },
    ],
    diagrams: [
      {
        svg: SVG_KAMABA_DRAINAGE,
        caption: "最も簡便な釜場排水（3.2.2）",
      },
    ],
  },

  "3.2.3": {
    plainSummary:
      "埋戻し前に型枠等を撤去する。材料と工法は種別（A種：山砂の類・水締め可〜D種：再生コンクリート砂）により特記による。300mm程度ごとに締め固め、土質に応じて余盛りする。",
    why: "埋戻しの失敗＝後日の沈下です。外構の土間が割れる、配管が折れる、といった不具合の多くは「厚く埋めて一気に転圧」が原因。300mmごとの締固めは地味ですが効きます。良質土かどうか怪しい場合は協議に回します。",
    points: [
      "1層300mm・締固め・次の層、のサイクルを守る",
      "水締めができるのはA種（山砂の類）とD種（再生コンクリート砂）",
      "埋戻し中の型枠・木片の埋め殺しはシロアリ・沈下の原因",
    ],
    glossary: [
      {
        term: "水締め",
        meaning:
          "水を撒きながら砂を締め固める方法。砂質土に有効。粘性土には使えない。",
      },
      {
        term: "余盛り",
        meaning: "将来の沈下を見込んで、仕上がりより高めに盛っておくこと。",
      },
    ],
    diagrams: [
      {
        svg: SVG_UMEMODOSHI,
        caption: "埋戻しは300mmごとに締固め（3.2.3）",
      },
    ],
  },

  "3.2.4": {
    plainSummary: "建物の周囲は幅2ｍ程度を水はけよく地均しする。",
    why: "建物際に水が溜まると基礎回りの地盤が緩み、湿気・凍害の原因にもなります。建物から外へ向かう水勾配をつけるのが基本です。",
  },

  "3.2.5": {
    plainSummary:
      "建設発生土（残土）の処理は特記による。",
    points: [
      "残土の搬出先・受入条件（土質・契約）を着工前に確定させる",
      "場内でのストック位置は次工程の邪魔にならない場所に",
    ],
  },

  "3.3.1": {
    plainSummary:
      "山留めは労働安全衛生法等に基づき安全に設置する。地盤調査報告書・現場の土質から総合判断し、適切な構造計算を行って所定の耐力を持たせる。",
    why: "山留めの崩壊は作業員の生き埋め・道路陥没など最悪の事故につながります。「経験でなんとかなる」ではなく、構造計算に基づく計画が要求されています。",
    glossary: [
      {
        term: "山留め",
        meaning:
          "掘削で周囲の地盤が崩れないように支える仮設の壁と支保工の総称。親杭横矢板・鋼矢板（シートパイル）・SMWなどの種類がある。",
      },
      {
        term: "切りばり・腹起し",
        meaning:
          "山留め壁を支える水平材。壁に沿うのが腹起し、対面の腹起し同士を突っ張るのが切りばり。",
      },
    ],
    diagrams: [
      {
        svg: SVG_YAMADOME,
        caption: "山留めの構成と力の流れ（3.3.1）",
      },
    ],
  },

  "3.3.2": {
    plainSummary:
      "山留めの設置期間中は、周辺地盤と山留めの状態を常に点検・計測する。異常を発見したら直ちに措置し、監督職員に報告する。",
    why: "山留めは壊れる前に必ずサインを出します（変位の増加・切りばりの軸力変化・周辺の地割れ）。日常の点検・計測はそのサインを捉える唯一の手段です。「毎日見る・測る・記録する」を徹底しましょう。",
    points: [
      "点検項目例：壁の変位、切りばりのたわみ・温度、周辺道路のひび、隣接建物の傾き",
      "大雨・地震の後は臨時点検",
    ],
  },

  "3.3.3": {
    plainSummary:
      "山留めの撤去は、撤去しても安全なことを確認してから慎重に行う。鋼材の引抜き跡は地盤変形を防ぐ措置（砂充填等）を講ずる。",
    why: "シートパイルを抜くと、その体積分の穴が地中に残り、周辺地盤が引き込まれて沈下します。「抜いたら埋める」までが山留め工事です。",
  },
};

export default ch03;
